import { Box, HBox, VBox } from "@ui/box/Box";
import { FormIntro, Glyph } from "../ds/Kit";
import Button from "@ui/button/Button";
import { Error as ErrorNotice } from "@ui/error/Error";
import { IconCalendar } from "@ui/icons/IconCalendar";
import { IconEdit } from "@ui/icons/IconEdit";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import { SpinnerProgress } from "@ui/spinner/SpinnerProgress";
import { Typo } from "@ui/text/Typo";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { useWindowSize } from "@ui/utils/useResize";
import { SignalHandler } from "badmfck-signal";
import { FC, useEffect, useState } from "react";
import { DEFAULT_SORT, findSort, sortQuery, sortRows, sortSelectValues, textMatches, type SortOption } from "src/utils/listSort";
import { usePagedList } from "../hooks/usePagedList";
import { ListPager } from "./ListPager";
import { toast } from "react-toastify";
import { DEFAULT_CURRENCY, MAX_USER_DETAILS_WIDTH } from "src/constants";
import { GD } from "src/GD";
import { USER_ROLE } from "src/services/AuthService";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { FormParser } from "src/view/FormParser";
import { useBuyerFinancials } from "../hooks/useBuyerFinancials";
import { StatusBadge } from "./StatusBadge";
import { UserDetails } from "./UserDetails";
import { S_UNIVERSAL_DIALOG } from "./universalDialog/signals";
import { useIssuer } from "../issuer/IssuerProvider";
import { confirmAction } from "../popup/general/ConfirmDialog";

const USER_LIST_BORDER = "#E8EDF3";
const USER_LIST_BRAND = "#27445D";
const USER_LIST_TEXT = "#172033";
const USER_LIST_MUTED = "#64748B";
const USER_LIST_HEADER = "#F8FAFC";
const USER_LIST_COLUMNS =
  "minmax(200px, 1.8fr) minmax(90px, .8fr) minmax(76px, .6fr) minmax(44px, .35fr) minmax(76px, .6fr) minmax(150px, 1.3fr) minmax(112px, .9fr)";

const formatUserDate = (value?: string | Date | null) => {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatUserTime = (value?: string | Date | null) => {
  if (!value) return "";
  return new Date(value).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (value: number | undefined, currency: string) =>
  value === undefined
    ? "—"
    : formatCurrencyWithNumberToSymbolWithNumber(value, currency);

type UserFilter = {
  name: string;
  role: string;
  status: string;
  sort: string;
};

const USER_SORT_OPTIONS: SortOption<IUserSaved>[] = [
  { label: "Name A–Z", sort: "name", order: "asc", get: (u) => u.name },
  { label: "Newest first", sort: "created_at", order: "desc", get: (u) => u.created_at },
  { label: "Last login", sort: "last_login_at", order: "desc", get: (u) => u.last_login_at },
  { label: "Team A–Z", sort: "team", order: "asc", get: (u) => u.team_name },
  { label: "Team balance", sort: "team_balance", order: "desc", get: (u) => Number(u.team_balance ?? 0) },
  { label: "Role", sort: "role", order: "asc", get: (u) => u.company_role },
];

const USER_PAGE_SIZE = 25;

const summarizeUsers = (users: IUserSaved[]): IUserListSummary => ({
  users: users.length,
  admins: users.filter((user) => user.company_role === "admin").length,
  teamleads: users.filter((user) => user.company_role === "teamlead").length,
  buyers: users.filter((user) => user.company_role === "buyer").length,
  active: users.filter((user) => user.company_status === "active").length,
  team_balance: users.reduce((sum, user) => sum + Number(user.team_balance ?? 0), 0),
});

interface UsersListProps {
  initialRole?: string;
  onRoleChange?: (role: string) => void;
}

const HeaderCell: FC<{ children: string; align?: "left" | "right" }> = ({
  children,
  align = "left",
}) => (
  <Typo
    sx={{
      color: "#7C899B",
      fontSize: "10px",
      fontWeight: 750,
      letterSpacing: "0.035em",
      textTransform: "uppercase",
      textAlign: align,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typo>
);

export const UsersList: FC<UsersListProps> = ({
  initialRole = "all",
  onRoleChange,
}) => {
  const { width: viewportWidth } = useWindowSize();
  const { selectedIssuer, selectedIssuerId } = useIssuer();
  const issuerCurrency = selectedIssuer?.currency || DEFAULT_CURRENCY;
  const isMobile = viewportWidth <= 768;
  const [profile] = GD.REQ_PROFILE.useRequest();
  const { cards: buyerCards, cardFinancials: buyerCardFinancials } =
    useBuyerFinancials();
  const [filter, setFilter] = useState<UserFilter>({
    name: "",
    role: initialRole,
    status: "all",
    sort: "",
  });
  const [debouncedFilter, setDebouncedFilter] = useState(filter);

  const roles = [
    { label: "All Roles", value: "all" },
    { label: "Admin", value: "admin" },
    { label: "Buyer", value: "buyer" },
    { label: "Team Lead", value: "teamlead" },
  ];

  const statuses = [
    { label: "All Statuses", value: "all" },
    { label: "Active", value: "active" },
    { label: "Disabled", value: "disabled" },
  ];

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedFilter(filter), 500);
    return () => clearTimeout(timeout);
  }, [filter]);

  useEffect(() => {
    setFilter((previous) => ({ ...previous, role: initialRole }));
  }, [initialRole]);

  const search = debouncedFilter.name.trim();
  const role = debouncedFilter.role === "all" ? "" : debouncedFilter.role;
  const status = debouncedFilter.status === "all" ? "" : debouncedFilter.status;
  const sort = findSort(USER_SORT_OPTIONS, debouncedFilter.sort);
  const list = usePagedList<IUserSaved, IUserListSummary, IUserListQuery>({
    request: (query) => GD.REQ_USERS_PAGE.request(query),
    query: {
      include_teams: "true",
      ...(selectedIssuerId === null ? {} : { issuer: selectedIssuerId }),
      ...(search ? { search } : {}),
      ...(role ? { role } : {}),
      ...(status ? { status } : {}),
      ...sortQuery(sort),
    },
    pageSize: USER_PAGE_SIZE,
    // A backend without paging repeats a user once per team.
    legacy: (rows) => {
      const seen = new Set<string>();
      return sortRows(rows.filter((user) => {
        const matches = textMatches(search, [user.name, user.email, user.uid, user.team_name]) &&
          (!role || user.company_role === role) &&
          (!status || user.company_status === status);
        if (!matches || seen.has(user.uid)) return false;
        seen.add(user.uid);
        return true;
      }), sort);
    },
    summarize: summarizeUsers,
  });
  const { reload } = list;
  const filteredUsers = list.rows;
  const busy = list.initialLoading;
  const listError = list.rows.length ? undefined : list.error;

  useEffect(() => {
    const signalHandler = new SignalHandler();
    // Users changed elsewhere; the payload is the whole list, so read this page again.
    signalHandler.add(GD.S_USERS_LIST_UPDATED, () => void reload());
    return () => signalHandler.clear();
  }, [reload]);

  const handleChangeRole = (user: IUserSaved) => {
    S_UNIVERSAL_DIALOG.invoke({
      label: `Change role — ${user.name}`,
      content: (
        <>
          <FormIntro icon={Glyph.shield} title="Change role" description="Roles decide what this person can see and do in the workspace." />
          <FormParser<IUserRoleForm>
            key={`changeUserRole-${user.uid}`}
            onRequest={async () => {
              const response = await GD.REQ_GET_USER_ROLE.request({
                uid: user.uid,
              });
              if (response.error || !response.data) {
                throw new Error(
                  getFriendlyErrorMessage(
                    response.error,
                    "Failed to load user role.",
                  ),
                );
              }
              return { ...response.data, uid: user.uid };
            }}
            onSubmit={async (data) => {
              if (
                user.company_role === USER_ROLE.BUYER &&
                data.company_role !== USER_ROLE.BUYER &&
                // The API sweeps the buyer balance before changing the role.
                !(await confirmAction({
                  title: "Change buyer role",
                  message: "Changing this buyer's role sends all of their available balance back to the company. Continue?",
                  confirm: "Change role",
                }))
              ) {
                return { error: { code: 0, message: "Role change cancelled" } };
              }
              const response = await GD.REQ_SET_USER_ROLE.request({
                uid: user.uid,
                company_role: data.company_role,
              });
              if (response.error) return response;
              toast.success("User role changed successfully");
              S_UNIVERSAL_DIALOG.invoke(null);
              return response;
            }}
            primaryButton={{ title: "Save role" }}
            secondaryButton={{
              title: "Cancel",
              onClick: () => S_UNIVERSAL_DIALOG.invoke(null),
            }}
          />
        </>
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  const openUserDetails = (user: IUserSaved) => {
    S_UNIVERSAL_DIALOG.invoke({
      sx: { maxWidth: MAX_USER_DETAILS_WIDTH },
      contentPadding: "0px",
      content: (
        <UserDetails
          userUid={user.uid}
          onClose={() => S_UNIVERSAL_DIALOG.invoke(null)}
          onUpdate={async () => {
            await Promise.all([GD.REQ_USERS.request(), reload()]);
          }}
        />
      ),
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
    });
  };

  const resetFilters = () => {
    setFilter({ name: "", role: "all", status: "all", sort: "" });
    onRoleChange?.("all");
  };

  return (
    <VBox
      sx={{
        height: isMobile ? "auto" : "100%",
        minHeight: isMobile ? "auto" : 0,
        overflow: isMobile ? "visible" : "hidden",
      }}
    >
      <HBox
        gap="10px"
        sx={{
          padding: "12px 16px",
          backgroundColor: "#FFFFFF",
          borderBottom: `1px solid ${USER_LIST_BORDER}`,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <Input
          name="name"
          placeholder="Name, email, ID or team"
          label="Search"
          size="small"
          data={filter}
          onChange={(value) => setFilter({ ...filter, name: value })}
          sx={{
            width: "280px",
            "@media (max-width: 600px)": { width: "100%", flexBasis: "100%" },
          }}
        />
        <Box
          sx={{
            width: "140px",
            flexShrink: 0,
            "@media (max-width: 600px)": {
              width: "calc(50% - 5px)",
              flex: "1 1 calc(50% - 5px)",
              minWidth: 0,
            },
          }}
        >
          <Select
            label="Role"
            name="role"
            size="small"
            data={filter}
            values={roles}
            onChange={(value) => {
              const role = value as string;
              setFilter({ ...filter, role });
              onRoleChange?.(role);
            }}
            sx={{ width: "100%" }}
          />
        </Box>
        <Box
          sx={{
            width: "140px",
            flexShrink: 0,
            "@media (max-width: 600px)": {
              width: "calc(50% - 5px)",
              flex: "1 1 calc(50% - 5px)",
              minWidth: 0,
            },
          }}
        >
          <Select
            label="Status"
            name="status"
            size="small"
            data={filter}
            values={statuses}
            onChange={(value) =>
              setFilter({ ...filter, status: value as string })
            }
            sx={{ width: "100%" }}
          />
        </Box>
        <Box sx={{ width: "160px", flexShrink: 0, "@media (max-width: 600px)": { width: "100%" } }}>
          <Select
            label="Sort by"
            name="sort"
            size="small"
            data={{ sort: filter.sort || DEFAULT_SORT }}
            values={sortSelectValues(USER_SORT_OPTIONS)}
            onChange={(value) => setFilter({ ...filter, sort: value === DEFAULT_SORT ? "" : String(value) })}
            sx={{ width: "100%" }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {(filter.name || filter.role !== "all" || filter.status !== "all") && (
          <Button
            title="Reset"
            variant="secondarySmall"
            onClick={resetFilters}
            sx={{ padding: "7px 10px", fontSize: "11px" }}
          />
        )}
        <Typo
          sx={{
            color: USER_LIST_MUTED,
            fontSize: "11px",
            lineHeight: "34px",
            whiteSpace: "nowrap",
          }}
        >
          {busy ? "Loading users…" : listError ? "Users unavailable" : `${list.total} ${list.total === 1 ? "user" : "users"}`}
        </Typo>
      </HBox>

      <Box
        sx={{
          flex: isMobile ? "none" : 1,
          minHeight: isMobile ? "auto" : 0,
          display: "flex",
          flexDirection: "column",
          overflow: isMobile ? "visible" : "hidden",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: USER_LIST_COLUMNS,
            alignItems: "center",
            columnGap: "12px",
            padding: "10px 12px",
            backgroundColor: USER_LIST_HEADER,
            borderBottom: `1px solid ${USER_LIST_BORDER}`,
            flexShrink: 0,
            "@media (max-width: 768px)": { display: "none" },
          }}
        >
          <HeaderCell>User</HeaderCell>
          <HeaderCell>Team</HeaderCell>
          <HeaderCell>Available</HeaderCell>
          <HeaderCell>Cards</HeaderCell>
          <HeaderCell>On cards</HeaderCell>
          <HeaderCell>Access</HeaderCell>
          <HeaderCell align="right">Activity</HeaderCell>
        </Box>

        <Box
          sx={{
            flex: isMobile ? "none" : 1,
            minHeight: isMobile ? "auto" : 0,
            overflowY: isMobile ? "visible" : "auto",
            overflowX: "hidden",
            padding: "0 10px 10px",
          }}
        >
          <SpinnerProgress busy={list.loading} />
          {listError && (
            <ErrorNotice error={listError} sx={{ margin: "12px" }} />
          )}
          {!busy && !listError && filteredUsers.length === 0 && (
            <VBox center sx={{ padding: "48px 20px" }} gap="5px">
              <Typo sx={{ color: USER_LIST_TEXT, fontWeight: 700 }}>
                No users found
              </Typo>
              <Typo sx={{ color: USER_LIST_MUTED, fontSize: "12px" }}>
                Try changing the filters.
              </Typo>
            </VBox>
          )}

          {!busy &&
            !listError &&
            filteredUsers.map((user) => {
              const isBuyer = user.company_role === USER_ROLE.BUYER;
              const cardFinancials = buyerCardFinancials.get(user.uid);
              const cardsCount = selectedIssuerId === null
                ? user.cards_count ?? cardFinancials?.cardsCount
                : cardFinancials?.cardsCount ?? (buyerCards ? 0 : undefined);
              const cardsBalance = selectedIssuerId === null
                ? user.cards_balance ?? cardFinancials?.cardsBalance
                : cardFinancials?.cardsBalance ?? (buyerCards ? 0 : undefined);
              // users/list is issuer-scoped, so its scalar balance belongs to
              // the selected issuer. Do not read another issuer from the array.
              const availableBalance = user.team_balance;
              const initials = user.name
                ? user.name.substring(0, 2).toUpperCase()
                : "U";

              if (isMobile) {
                return (
                  <Box
                    key={user.uid}
                    role="button"
                    tabIndex={0}
                    onClick={() => openUserDetails(user)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ")
                        openUserDetails(user);
                    }}
                    sx={{
                      marginTop: "10px",
                      padding: "14px",
                      border: `1px solid ${USER_LIST_BORDER}`,
                      borderRadius: "12px",
                      backgroundColor: "#FFFFFF",
                      boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
                      cursor: "pointer",
                      "&:focus-visible": {
                        outline: `2px solid ${USER_LIST_BRAND}`,
                        outlineOffset: "2px",
                      },
                    }}
                  >
                    <HBox
                      sx={{ alignItems: "center", gap: "11px", minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "11px",
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          backgroundColor: "#F1F5F9",
                          color: USER_LIST_BRAND,
                          fontWeight: 800,
                          fontSize: "12px",
                        }}
                      >
                        {initials}
                      </Box>
                      <VBox gap="2px" sx={{ minWidth: 0, flex: 1 }}>
                        <Typo
                          sx={{
                            color: USER_LIST_TEXT,
                            fontSize: "14px",
                            fontWeight: 750,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.name || "N/A"}
                        </Typo>
                        <Typo
                          sx={{
                            color: USER_LIST_MUTED,
                            fontSize: "10px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {user.email || user.uid}
                        </Typo>
                      </VBox>
                      <VBox
                        gap="5px"
                        sx={{ alignItems: "flex-end", flexShrink: 0 }}
                      >
                        <StatusBadge status={user.company_role} type="role" />
                        <StatusBadge status={user.company_status} />
                      </VBox>
                    </HBox>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                        gap: "9px",
                        marginTop: "13px",
                      }}
                    >
                      <MobileUserMetric
                        label="Team"
                        value={user.team_name || "No team"}
                      />
                      <MobileUserMetric
                        label="Available"
                        value={
                          isBuyer
                            ? availableBalance === undefined
                              ? "—"
                              : formatAmount(availableBalance, issuerCurrency)
                            : "—"
                        }
                      />
                      <MobileUserMetric
                        label="Cards"
                        value={isBuyer ? String(cardsCount ?? "—") : "—"}
                      />
                      <MobileUserMetric
                        label="On cards"
                        value={
                          isBuyer ? formatAmount(cardsBalance, issuerCurrency) : "—"
                        }
                      />
                    </Box>
                    <HBox
                      sx={{
                        marginTop: "12px",
                        paddingTop: "11px",
                        borderTop: `1px solid ${USER_LIST_BORDER}`,
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "10px",
                      }}
                    >
                      <VBox gap="2px">
                        <Typo sx={{ color: USER_LIST_MUTED, fontSize: "11px" }}>
                          Last activity
                        </Typo>
                        <Typo
                          sx={{
                            color: USER_LIST_TEXT,
                            fontSize: "11px",
                            fontWeight: 650,
                          }}
                        >
                          {formatUserDate(user.last_login_at)}{" "}
                          {formatUserTime(user.last_login_at)}
                        </Typo>
                      </VBox>
                      {profile?.company_role === USER_ROLE.ADMIN &&
                        profile.uid !== user.uid && (
                          <Button
                            title="Change role"
                            variant="secondarySmall"
                            onClick={(event) => {
                              event?.stopPropagation();
                              handleChangeRole(user);
                            }}
                            sx={{ minHeight: "38px", padding: "7px 10px" }}
                          />
                        )}
                    </HBox>
                  </Box>
                );
              }

              return (
                <Box
                  key={user.uid}
                  role="button"
                  tabIndex={0}
                  onClick={() => openUserDetails(user)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ")
                      openUserDetails(user);
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: USER_LIST_COLUMNS,
                    alignItems: "center",
                    columnGap: "12px",
                    minHeight: "62px",
                    padding: "0 12px",
                    borderBottom: `1px solid ${USER_LIST_BORDER}`,
                    cursor: "pointer",
                    transition:
                      "background-color .15s ease, box-shadow .15s ease",
                    "&:hover": { backgroundColor: "#F8FAFC" },
                    "&:focus-visible": {
                      outline: `2px solid ${USER_LIST_BRAND}`,
                      outlineOffset: "-2px",
                    },
                  }}
                >
                  <HBox sx={{ alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <Box
                      sx={{
                        width: "34px",
                        height: "34px",
                        borderRadius: "9px",
                        backgroundColor: "#F1F5F9",
                        color: USER_LIST_BRAND,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: "11px",
                        flexShrink: 0,
                      }}
                    >
                      {initials}
                    </Box>
                    <VBox gap="2px" sx={{ minWidth: 0 }}>
                      <Typo
                        sx={{
                          color: USER_LIST_TEXT,
                          fontSize: "13px",
                          fontWeight: 750,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.name || "N/A"}
                      </Typo>
                      <Typo
                        sx={{
                          color: USER_LIST_MUTED,
                          fontSize: "10px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.email || "N/A"} · {user.uid?.substring(0, 8)}
                      </Typo>
                    </VBox>
                  </HBox>

                  <Typo
                    sx={{
                      color: USER_LIST_TEXT,
                      fontSize: "12px",
                      fontWeight: 650,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {user.team_name || "—"}
                  </Typo>
                  <Typo
                    sx={{
                      color: isBuyer ? USER_LIST_TEXT : "#A8B1BF",
                      fontSize: "12px",
                      fontWeight: isBuyer ? 750 : 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isBuyer
                      ? availableBalance === undefined
                        ? "—"
                        : formatAmount(availableBalance, issuerCurrency)
                      : "—"}
                  </Typo>
                  <Typo
                    sx={{
                      color: isBuyer ? USER_LIST_TEXT : "#A8B1BF",
                      fontSize: "12px",
                      fontWeight: isBuyer ? 750 : 500,
                    }}
                  >
                    {isBuyer ? (cardsCount ?? "—") : "—"}
                  </Typo>
                  <Typo
                    sx={{
                      color: isBuyer ? USER_LIST_TEXT : "#A8B1BF",
                      fontSize: "12px",
                      fontWeight: isBuyer ? 750 : 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isBuyer ? formatAmount(cardsBalance, issuerCurrency) : "—"}
                  </Typo>

                  <HBox
                    sx={{
                      alignItems: "center",
                      gap: "5px",
                      minWidth: 0,
                      flexWrap: "wrap",
                    }}
                  >
                    <StatusBadge status={user.company_role} type="role" />
                    <StatusBadge status={user.company_status} />
                    {profile?.company_role === USER_ROLE.ADMIN &&
                      profile.uid !== user.uid && (
                        <Button
                          ariaLabel="Change role"
                          variant="secondarySmall"
                          icon={<IconEdit width={13} height={13} />}
                          onClick={(event) => {
                            event?.stopPropagation();
                            handleChangeRole(user);
                          }}
                          sx={{
                            width: "26px",
                            height: "26px",
                            minHeight: "26px",
                            padding: 0,
                            borderRadius: "8px",
                            color: USER_LIST_MUTED,
                            "&:hover": { color: USER_LIST_BRAND },
                          }}
                        />
                      )}
                  </HBox>

                  <VBox gap="2px" sx={{ minWidth: 0, alignItems: "flex-end" }}>
                    <HBox sx={{ alignItems: "center", gap: "5px" }}>
                      <Box sx={{ color: USER_LIST_MUTED, display: "flex" }}>
                        <IconCalendar width={13} height={13} />
                      </Box>
                      <Typo
                        sx={{
                          color: USER_LIST_TEXT,
                          fontSize: "11px",
                          fontWeight: 650,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatUserDate(user.last_login_at)}{" "}
                        {formatUserTime(user.last_login_at)}
                      </Typo>
                    </HBox>
                    <Typo
                      sx={{
                        color: USER_LIST_MUTED,
                        fontSize: "11px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Joined {formatUserDate(user.created_at)}
                    </Typo>
                  </VBox>
                </Box>
              );
            })}
        </Box>
        <Box sx={{ borderTop: list.total > 0 ? `1px solid ${USER_LIST_BORDER}` : "none" }}>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} label="Users" />
        </Box>
      </Box>
    </VBox>
  );
};

const MobileUserMetric = ({
  label,
  value,
}: {
  label: string;
  value: string;
}) => (
  <VBox
    gap="3px"
    sx={{
      minWidth: 0,
      padding: "10px 11px",
      borderRadius: "10px",
      backgroundColor: USER_LIST_HEADER,
    }}
  >
    <Typo sx={{ color: USER_LIST_MUTED, fontSize: "9px", fontWeight: 650 }}>
      {label}
    </Typo>
    <Typo
      sx={{
        color: USER_LIST_TEXT,
        fontSize: "12px",
        fontWeight: 750,
        // Team names routinely run a couple of pixels past the tile: wrap to a
        // second line instead of clipping the last character or two.
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Typo>
  </VBox>
);
